/**
 * 포즈 표현과 시각 조회 — 공통 포즈 모양, timestamp 구간 탐색, 쿼터니언 정규화.
 *
 * sigmakit `viewer3d/babylon/playback.ts` 의 수학을 Babylon 타입 없이 옮긴 것이다. 인덱스 비례
 * 조회는 샘플 간격이 불균일하면 영상과 어긋나므로 **timestamp 로 조회**한다. 두 표본 사이를
 * 보간하지 않는다 — 없던 포즈를 지어내 보이면 그것이 원본처럼 읽힌다. Babylon 바인딩(Vector3 ·
 * Quaternion)은 `sim/` 안에서만 한다(규칙 §9).
 *
 * Owns:
 * - timestamp 구간 탐색 · 쿼터니언 정규화(WXYZ).
 *
 * Does not own:
 * - 어느 채널이 포즈인가 — 매핑. 표본 유효성 · gap — `poseSampling.ts`.
 */

/** `[x, y, z, qw, qx, qy, qz]` — 자세가 없는 트랙은 항등 쿼터니언 `[1, 0, 0, 0]` 이다. */
export type PoseWxyz = readonly [number, number, number, number, number, number, number];

export const IDENTITY_QUATERNION_WXYZ: readonly [number, number, number, number] = [1, 0, 0, 0];

/** `seconds` 가 놓이는 구간 `[lo, hi]`. 범위 밖은 양 끝에 clamp. */
export function findTimeSpan(
  timestamps: readonly number[],
  seconds: number,
): { lo: number; hi: number } {
  if (timestamps.length === 0) {
    return { lo: 0, hi: 0 };
  }
  let lo = 0;
  let hi = timestamps.length - 1;
  if (seconds <= timestamps[lo]) {
    return { lo, hi: lo };
  }
  if (seconds >= timestamps[hi]) {
    return { lo: hi, hi };
  }
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (timestamps[mid] <= seconds) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  return { lo, hi };
}

export function normalizeQuaternionWxyz(
  q: readonly [number, number, number, number],
): [number, number, number, number] {
  const norm = Math.hypot(q[0], q[1], q[2], q[3]);
  if (norm === 0 || !Number.isFinite(norm)) {
    throw new RangeError("Quaternion must have a finite, nonzero norm");
  }
  return [q[0] / norm, q[1] / norm, q[2] / norm, q[3] / norm];
}
